import { PropertyUseCaseInterface } from 'application/usecases/PropertyUseCase';
import FallbackImg from 'assets/images/FallbackImg.svg';
import { openErrorNoti } from 'presentation/components/Notification';
import { TooltipText } from 'presentation/components/TooltipText';
import { completeFetching, startFetching } from 'presentation/redux/features/app/appSlice';
import { useAppDispatch, useAppSelector } from 'presentation/redux/hooks';
import { AUTH_ROUTES } from 'presentation/router/CONSTANTS';
import { MouseEventHandler, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COMMON } from 'utilities/constants/COMMON';
import { commonHelpers } from 'utilities/helpers';
import { PropertyData } from '../models/Property';
import { BadgeTiming } from './AuctionTime';
import { CountLike } from './CountLike';
import { RateBadge } from './Rate';
import { useBuildingAge } from 'hook/useBuildingAge';

interface props {
    data: PropertyData;
    onClick?: MouseEventHandler<any>;
    propertyUseCase?: PropertyUseCaseInterface;
}
export const CardComponent = ({ data, onClick, propertyUseCase }: props) => {
    const [address, setAddress] = useState<string>(COMMON.EMPTY);
    const imageUrl =
        data?.mstProperty?.propertyImages?.length >= 1
            ? data?.mstProperty?.propertyImages[0]?.imageFilePath
            : '';

    const [isBookmarked, setIsBookmarked] = useState<boolean>(false);
    const [countLike, setCountLike] = useState<number>(0);
    const userSlice = useAppSelector((state) => state.user);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const buildingAge = data?.mstProperty?.builtAt ? useBuildingAge({ builtAt: data?.mstProperty.builtAt }) : COMMON.EMPTY; // 築年数の計算

    useEffect(() => {
        setIsBookmarked(data.currentBookmark ?? false);
        setCountLike(data.countLike);
    }, [data]);

    useEffect(() => {
        let result = '';
        const mstProperty = data?.mstProperty;
        if (mstProperty) {
            if (mstProperty.residence) result += mstProperty.residence + ' ';
            if (mstProperty.builtAt) {
                    result += buildingAge + ' ';
            }
        }
        
        if (data.occupiedArea)
            result += commonHelpers.formatNumberMoney(Number(data.occupiedArea)) + 'm² ';
        if (result.trim() !== '') setAddress(result);
    }, [data?.mstProperty, buildingAge]);

    const onclickBookmark = async (e: any) => {
        try {
            e.stopPropagation();
            e.preventDefault();

            if (userSlice?.isSignedIn) {
                dispatch(startFetching());
                if (propertyUseCase) {
                    const response = await propertyUseCase.memberBookMarkListedProperty({
                        listedPropertyId: data.id,
                    });
                    if (response.status == '200') {
                        const likeCount = await propertyUseCase.countLike(data.id);
                        if (likeCount.status == '200') {
                            setIsBookmarked(!isBookmarked);
                            setCountLike(likeCount.result);
                        }
                    }
                }
            } else {
                navigate(`${AUTH_ROUTES.PATH}/${AUTH_ROUTES.SUB.CU_001_00}`);
            }
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    return (
        <div className="card-element button-style glown-on-hover" onClick={onClick}>
            <section
                style={{
                    width: '100%',
                    height: 200,
                    padding: 6,
                    backgroundImage: `url("${imageUrl}"), url("${FallbackImg}")`,
                    borderRadius: '8px 8px 0px 0px',
                    backgroundSize: 'cover',
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'column',
                    objectFit: 'cover'
                }}
            >
                <div
                    style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}
                    onClick={onclickBookmark}
                >
                    <CountLike value={countLike} currentBookmarked={isBookmarked} />
                </div>
                <BadgeTiming auctionTime={data.auctionTime} auctionStatus={data.auctionStatus} />
            </section>
            <section style={{ padding: '24px 16px 24px 16px', width: '100%', height: 150 }}>
                <div className="label-card" style={{ marginBottom: 4, minHeight: 17 }}>
                    <TooltipText content={address} />
                </div>
                <div className="label-card" style={{ marginBottom: 4, minHeight: 17 }}>
                    {data?.mstProperty?.name ?? COMMON.EMPTY}
                </div>
                <div
                    style={{
                        marginBottom: 12,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <section className="label-card">即決価格</section>
                    <section style={{ display: 'flex', alignContent: 'center' }}>
                        <div style={{ marginRight: 8 }}>
                            <span className="card-number">
                                {commonHelpers.formatMoney(
                                    commonHelpers.roundTenThousandYen(data.priceBuyNow),
                                )}
                            </span>
                            <span className="card-unit">万円</span>
                        </div>
                        <RateBadge value={data.rateBuyNow} />
                    </section>
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <section className="label-card">現在の最高入札額</section>
                    <section style={{ display: 'flex', alignContent: 'center' }}>
                        <div style={{ marginRight: 8 }}>
                            <span className="card-number">
                                {commonHelpers.formatMoney(
                                    commonHelpers.roundTenThousandYen(
                                        data.currentHighestAuctionPrice,
                                    ),
                                )}
                            </span>
                            <span className="card-unit">万円</span>
                        </div>
                        <RateBadge value={data.rateHighestAuctionNow} />
                    </section>
                </div>
            </section>
        </div>
    );
};
